import { useMemo } from "react";
import type { MoversDTO } from "../../shared/types";
import { sectorColor } from "../lib/colors";
import { formatPct, returnColor } from "../lib/format";
import { SectionHeader } from "./ui";

/** Aggregates open positions by sector into ranked bars with a long/short split. */
export function SectorBattle({ data }: { data: MoversDTO | null }) {
  const rows = useMemo(() => {
    const map = new Map<string, { sum: number; n: number; long: number; short: number }>();
    for (const p of data?.positions ?? []) {
      const sec = p.sector ?? "Unclassified";
      const g = map.get(sec) ?? { sum: 0, n: 0, long: 0, short: 0 };
      g.sum += p.return;
      g.n += 1;
      if (p.direction === "LONG") g.long += 1;
      else g.short += 1;
      map.set(sec, g);
    }
    return [...map.entries()]
      .map(([name, g]) => ({ name, avg: g.sum / g.n, long: g.long, short: g.short, n: g.n }))
      .sort((a, b) => b.avg - a.avg);
  }, [data]);

  if (rows.length === 0) return null;
  const max = rows.reduce((m, r) => Math.max(m, Math.abs(r.avg)), 0.0001);

  return (
    <div className="panel p-4">
      <SectionHeader
        title="Sector battle"
        right={<span className="text-[11px] text-faint">avg return · L/S</span>}
      />
      <div className="space-y-2.5">
        {rows.map((s, i) => {
          const up = s.avg >= 0;
          const w = `${Math.round((Math.abs(s.avg) / max) * 100)}%`;
          return (
            <div key={s.name} className="flex items-center gap-3">
              <div className="flex w-40 shrink-0 items-center gap-2 sm:w-52">
                <span
                  className="h-3 w-3 shrink-0 rounded-sm"
                  style={{ backgroundColor: sectorColor(i) }}
                />
                <span className="truncate text-sm text-fg" title={s.name}>
                  {s.name}
                </span>
              </div>
              <div className="relative h-6 flex-1 overflow-hidden rounded bg-ink-800">
                <div
                  className="absolute inset-y-0 left-0 rounded"
                  style={{
                    width: w,
                    backgroundColor: up ? "rgba(22,199,132,0.55)" : "rgba(234,57,67,0.5)",
                  }}
                />
              </div>
              <div
                className={`num w-16 shrink-0 text-right text-sm font-bold ${returnColor(s.avg)}`}
              >
                {formatPct(s.avg, 1)}
              </div>
              <div className="num hidden w-16 shrink-0 justify-end gap-1 text-[11px] sm:flex">
                <span className="text-gain">{s.long}L</span>
                <span className="text-faint">/</span>
                <span className="text-loss">{s.short}S</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
